<template>
  <div class="MyAccountPage_Billing">
    <transition name="fade" mode="out-in" v-if="isLoading">
      <app-loading
        :is-loading="isLoading"
        :spinner-color="'#1474a4'"
      />
    </transition>

    <template v-else>
      <h2 class="title">
        {{ $t('myaccount.billing.title', 'Payments') }}
      </h2>

      <payment-due
        :billings = this.hasBalanceInvoices
        v-if="isShowPastDueSection"
      />

      <ul v-if="billingInvoices.length > 0" class="billings">
        <li
          class="billingItem flex-between"
          v-for="(item, index) in notHasBalanceInvoices"
          v-if="item.pdfFileId"
          :key="index"
        >
          <span class="date">
            {{ item.invoiceDate | formatLocalDate }}
          </span>

          <span
            class="price"
            :class="{ 'notBalance': !item.hasBalance }">
            {{ item.price | formatCurrency }}
          </span>

          <a
            v-if="showInvoiceLink"
            :href="item.pdfFileId"
            @click.prevent.stop="downloadInvoiceTask.run(item.pdfFileId, item.pdfFileUrl)"
            class="downloadLink">
            {{ $t('myaccount.billing.load.link', 'Invoice') }}
            ›
          </a>
        </li>
      </ul>

      <p v-else class="empty-data">
        {{ $t('myaccount.billing.body.emptyData', 'Sorry, you do not have zuora account or your billing have not synchronize yet!') }}
      </p>
    </template>
  </div>
</template>

<script>
  import { extractErrorMessage } from '@/utilities/common'
  import Vue from 'vue'
  import { mapGetters } from 'vuex'
  import paymentDue from 'components/PaymentDue'
  import { detectedCulture } from 'utilities/common/culture'

  export default {
    name: 'billing',

    data () {
      return {
        isLoading: true,
        billings: []
      }
    },

    computed: {
      ...mapGetters('account', ['isShowPastDueSection', 'billingInvoices']),

      hasBalanceInvoices () {
        return this.billingInvoices.filter(item => item.hasBalance === true)
      },

      notHasBalanceInvoices () {
        return this.billingInvoices.filter(item => item.hasBalance === false)
      },

      showInvoiceLink () {
        return detectedCulture.countryCode !== 'DE' && detectedCulture.countryCode !== 'IT' && detectedCulture.countryCode !== 'GB' && detectedCulture.countryCode !== 'AT' 
      }
    },

    tasks (t) {
      return {
        downloadInvoiceTask: t(async function (fileId, fileUrl) {
          this.trackConversion({ name: 'download', url: fileUrl })
          document.querySelector(`[href='${fileId}']`).classList.add('downloading')
          await Vue.api.billing.getInvoiceFileById(fileId)
            .then((data) => {
              let downloadLink = document.createElement('a')
              downloadLink.style.display = 'none'
              downloadLink.href = data.link
              document.body.appendChild(downloadLink)
              downloadLink.click()

              setTimeout(() => {
                document.body.removeChild(downloadLink)
                document.querySelector(`[href='${fileId}']`).classList.remove('downloading')
                document.querySelector(`[href='${fileId}']`).classList.add('downloaded')
              }, 500)
            })
        }).onError(function (task) {
          let errorTitle
          if (task.error.status === 401) {
            errorTitle = extractErrorMessage(task.error)
          } else {
            errorTitle = this.$t('validation.error.message.invoiceFileIsNotFound', 'The invoice file is not found.')
          }
          this.$notify({
            type: 'error',
            title: errorTitle
          })
        }).flow('restart')
      }
    },

    created () {
      this.$store.dispatch('account/fetchBillingInvoices').then(() => {
        this.isLoading = false
      }).catch(error => {
        this.$notify({
          type: 'error',
          title: extractErrorMessage(error)
        })
      })
    },

    components: {
      paymentDue
    }
  }
</script>

<style lang="scss">
  .MyAccountPage {
    &_Billing {
      .title {
        border-bottom: .01rem solid #9b9b9b;
      }

      .billings {
        .billingItem {
          padding: .08rem .1rem;
          background: #fff;
          border-bottom: .01rem solid #9b9b9b;
          line-height: .32rem;
        }
      }

      .date {
        font-size: .18rem;
        color: #4a4a4a;
        font-weight: 500;
        flex-shrink: 0;
        min-width: 1.1rem;
      }

      .price {
        font-size: .16rem;
        font-weight: 700;
        padding: 0 .1rem;
        color: $font-black;
      }

      .downloadLink {
        cursor: pointer;
        color: $blue;
        font-size: .14rem;
        font-weight: 500;
        line-height: 1.5;

        &.downloading {
          color: #d8d8d8;
          pointer-events: none;
        }

        &.downloaded {
          color: $pink;
        }
      }

      .paymentDue {
        border: none;
        border-bottom: .01rem solid #9b9b9b;

        @include media(tablet) {
          margin-top: 0;
          margin-bottom: 0;
        }
      }
    }
  }
</style>
